import { useEffect, useMemo, useState, useRef } from "react"
import Box from "@mui/material/Box"
import Modal from "@mui/material/Modal"
import HourglassTopIcon from "@mui/icons-material/HourglassTop"
import IdxLogo from "./assets/index-logo.png"
import PrintsIcon from "@mui/icons-material/Style"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import SellOutlinedIcon from "@mui/icons-material/SellOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { useFirstTimeInView } from "./utils/useFirstTimeInView"
import { useSettings } from "./context/SettingsContext"
import { useAuthContext } from "./context/AuthContext"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { event } from "./auth"
import { useIgnoredContext } from "./context/IgnoredContext"
import CloseIcon from "@mui/icons-material/Close"
import { isSingleOnlyCard } from "./utils/isSingleOnlyCard"
import { CARD_RATIO } from "./constants"

import { useMillEffectsCtx } from "./context/MillEffects"
import { usePrepCounterCtx } from "./context/PrepCounters"
import { useFloatingMemCtx } from "./context/FloatingMemory"
import { useGraveBanishCtx } from "./context/GraveBanish"

const isDev = process.env.NODE_ENV === "development"

const tcgLink = (search) =>
  `https://www.tcgplayer.com/search/grand-archive/product?Language=English&productLineName=grand-archive&q=${search}&view=grid`

// const yetiLink = (search) => `https://www.yetigaming.com/products/${search}`

const IconCircle = ({ children }) => (
  <div
    style={{
      boxSizing: "borderBox",
      border: "2px solid white",
      height: 30,
      width: 30,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "50%",
    }}
  >
    {children}
  </div>
)

const Price = ({ value, link, favicon, title }) => {
  return (
    <a
      onClick={() => {
        event("price-link", "/deck")
      }}
      className="card-pill link"
      title={title}
      href={link}
      rel="noreferrer"
      target="_blank"
      style={{ top: 50 }}
    >
      {favicon} ${typeof value === "number" ? Number(value).toFixed(2) : value}
      <OpenInNewIcon sx={{ fontSize: 11, ml: "-1px" }} />
    </a>
  )
}

const PrintPill = ({ count, top, title, icon }) => (
  <div
    onClick={() => event("print-numbers", "/deck")}
    title={title}
    className="card-pill muted"
    style={{ top, cursor: "default" }}
  >
    {icon}
    {count ? count.toLocaleString("en-US") : "unknown"}
  </div>
)

// kept for future use (image loading states)
// eslint-disable-next-line no-unused-vars
const LoadingIcon = () => (
  <IconCircle>
    <HourglassTopIcon
      className="spinner"
      style={{
        color: "white",
      }}
    />
  </IconCircle>
)

const returnToBuilderStyles = () => ({
  background: "var(--surface-2)",
  color: "var(--text)",
  fontSize: "16px",
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
  height: "50px",
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderTopRightRadius: 8,
  borderTopLeftRadius: 8,
  gap: 10,
})

const InventoryWrapper = ({
  children,
  add,
  remove,
  count,
  isOnlyOneLimit,
  enabled,
}) => {
  const settings = useSettings()
  const authCtx = useAuthContext()
  const btnStyle = {
    border: "none",
    background: "transparent",
    color: "var(--pill-text)",
    cursor: "pointer",
    width: 24,
    fontSize: 15,
    height: "100%",
    lineHeight: 1,
    padding: 0,
  }

  const playSet = isOnlyOneLimit ? 1 : 4
  const hasPlaySet = count >= playSet

  // collection managing is account-bound: no steppers and no owned
  // pill while logged out (any pre-login local data stays untouched,
  // reserved for the migration prompt). cloudLoaded matters too: an
  // edit made before the first snapshot arrives would diff against
  // stale local state and overwrite the cloud collection.
  if (!authCtx?.user || !authCtx?.cloudLoaded) {
    return children
  }

  if (!enabled && !settings.state.showOwned) {
    return children
  }

  return (
    <div
      className="inv-wrapper"
      style={{
        display: "inline-block",
        position: "relative",
      }}
    >
      {enabled ? (
        <div
          className="inv-controls"
          style={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            width: !hasPlaySet ? 112 : 88,
            overflow: "hidden",
            background: "var(--pill-bg)",
            borderRadius: 6,
            height: 20,
            zIndex: 5,
            top: 74,
            right: "var(--pill-right)",
            boxShadow: "0 0 0 1px var(--pill-ring), var(--shadow-1)",
          }}
        >
          <button style={btnStyle} onClick={remove}>
            −
          </button>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              background: hasPlaySet
                ? "var(--success)"
                : "rgba(255,255,255,0.12)",
              color: "white",
              flex: 1,
              height: "100%",
              textAlign: "center",
              lineHeight: "20px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {count ?? 0} <span style={{ fontSize: 9 }}>/ {playSet}</span>
          </div>
          <button style={btnStyle} onClick={add}>
            +
          </button>
          {!hasPlaySet && (
            <div
              title="one-click play set"
              style={{ height: "100%", display: "flex", alignItems: "center" }}
            >
              <CheckCircleIcon
                fontSize="small"
                onClick={() => {
                  if (!isOnlyOneLimit) {
                    if (count < 4) {
                      for (let i = 0; i < 4 - count; i++) {
                        add()
                      }
                    }
                  } else {
                    if (count < 1) {
                      add()
                    }
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: "var(--success-bright)",
                  fontSize: 16,
                  mr: "3px",
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className="inv-controls card-pill muted"
          style={{ top: 74, zIndex: 5 }}
        >
          owned: {count}
        </div>
      )}
      {children}
    </div>
  )
}

// Dev-only manual labeling panel. Used to tag cards for the manual data
// files (mill, preparation counters, floating memory, grave banish).
// The maps stay reachable from the console as window.__millCtx,
// window.__prepCtx, window.__fmctx and window.__graveBanishCtx.
const DevLabelPanel = ({ name }) => {
  const millCtx = useMillEffectsCtx()
  const prepCtx = usePrepCounterCtx()
  const fmCtx = useFloatingMemCtx()
  const graveBanishCtx = useGraveBanishCtx()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    window.__millCtx = millCtx
    window.__prepCtx = prepCtx
    window.__fmctx = fmCtx
    window.__graveBanishCtx = graveBanishCtx
  })

  const toggleKind = (ctx, kind) => {
    ctx.setMap((map) => {
      if (map[name]?.[kind]) {
        const entry = { ...map[name] }
        delete entry[kind]
        const next = { ...map }
        // drop the card entirely when its last label is removed so
        // empty {} entries never reach the manual data files
        if (Object.keys(entry).length) {
          next[name] = entry
        } else {
          delete next[name]
        }
        return next
      }
      return { ...map, [name]: { ...map[name], [kind]: 1 } }
    })
  }

  const toggleFlat = (ctx) => {
    ctx.setMap((map) => {
      if (map[name]) {
        const next = { ...map }
        delete next[name]
        return next
      }
      return { ...map, [name]: 1 }
    })
  }

  const chips = [
    {
      label: "mill self",
      on: !!millCtx.map[name]?.self,
      toggle: () => toggleKind(millCtx, "self"),
    },
    {
      label: "mill opp",
      on: !!millCtx.map[name]?.opp,
      toggle: () => toggleKind(millCtx, "opp"),
    },
    {
      label: "FM pure",
      on: !!fmCtx.map[name]?.pure,
      toggle: () => toggleKind(fmCtx, "pure"),
    },
    {
      label: "FM class bonus",
      on: !!fmCtx.map[name]?.classBonus,
      toggle: () => toggleKind(fmCtx, "classBonus"),
    },
    {
      label: "prep uses",
      on: !!prepCtx.map[name]?.uses,
      toggle: () => toggleKind(prepCtx, "uses"),
    },
    {
      label: "prep adds",
      on: !!prepCtx.map[name]?.adds,
      toggle: () => toggleKind(prepCtx, "adds"),
    },
    {
      label: "grave banish",
      on: !!graveBanishCtx.map[name],
      toggle: () => toggleFlat(graveBanishCtx),
    },
  ]

  const activeCount = chips.filter((c) => c.on).length

  return (
    <div className="label-panel">
      <button
        type="button"
        className={`label-chip${activeCount ? " on" : ""}`}
        title="Dev only: label this card for the manual data files"
        onClick={() => setOpen((o) => !o)}
      >
        <SellOutlinedIcon sx={{ fontSize: 11, mr: "3px", mb: "-1px" }} />
        {activeCount || ""}
      </button>
      {open &&
        chips.map((chip) => (
          <button
            key={chip.label}
            type="button"
            className={`label-chip${chip.on ? " on" : ""}`}
            onClick={chip.toggle}
          >
            {chip.label}
          </button>
        ))}
    </div>
  )
}

export const Card = ({
  idxUrl,
  imgUrl,
  name,
  height,
  onMouseEnter,
  onMouseLeave,
  shadow,
  tcgPrice,
  yetiPrice,
  regularPrints,
  specialPrints,
  useCardsContext,
  card,
}) => {
  const triedFallback = useRef(false)
  const ctx = useCardsContext?.()
  const settings = useSettings()
  const { ignoreEnabled, addToIgnored } = useIgnoredContext()

  const [modal, setModal] = useState(false)
  const dimensions = {
    width: height * CARD_RATIO,
    height,
  }
  // shared by the placeholder div and the img so the placeholder
  // background never peeks out past the card's corners
  const cardRadius = height < 150 ? 4 : "4.75%/3.5%"
  const [hasBeenInView, ref] = useFirstTimeInView()

  function handleEnter() {
    onMouseEnter?.()
  }
  function handleLeave() {
    onMouseLeave?.()
  }

  const [flip, setFlip] = useState(false)
  const imgSrc = flip ? card.computed.backUrl : imgUrl

  const flipBtnStyle = (active) => ({
    all: "unset",
    fontFamily: "var(--font-ui)",
    fontWeight: 600,
    fontSize: 10,
    cursor: "pointer",
    height: 20,
    width: 44,
    textAlign: "center",
    background: active ? "var(--accent)" : "transparent",
    color: active ? "var(--accent-contrast)" : "var(--pill-text)",
  })

  const cardComp = (
    <div
      ref={ref}
      className="card"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        borderRadius: cardRadius,
        position: "relative",
        background: "var(--surface-2)",
        ...dimensions,
      }}
    >
      {card.computed.backUrl && (
        <div
          style={{
            position: "absolute",
            right: "var(--pill-right)",
            top: 200,
            height: 20,
            borderRadius: 6,
            fontSize: 10,
            display: "flex",
            alignItems: "center",
            boxShadow: "0 0 0 1px var(--pill-ring), var(--shadow-1)",
            overflow: "hidden",
            background: "var(--pill-bg)",
            zIndex: 5,
          }}
        >
          <button style={flipBtnStyle(!flip)} onClick={() => setFlip(false)}>
            front
          </button>
          <button style={flipBtnStyle(flip)} onClick={() => setFlip(true)}>
            back
          </button>
        </div>
      )}
      {/* ctx is only provided by the main card grid — champion lines
          and hover previews render without it and get no WR pill */}
      {ctx && settings.state.showWinRate && (
        <div
          className="card-pill meta"
          title="win rate, past 60 days"
          style={{ top: 170, cursor: "default" }}
        >
          {card.computed.meta?.wr60 ?? 0}% WR
        </div>
      )}
      {settings.state.showPrint && regularPrints !== undefined && (
        <>
          <PrintPill
            count={regularPrints}
            top={98}
            title="regular prints"
            icon={
              <PrintsIcon
                style={{ height: 13, width: 13, color: "var(--pill-muted)" }}
              />
            }
          />
          <PrintPill
            count={specialPrints}
            top={122}
            title="special prints (foil, promo, CSR, CUR, etc.)"
            icon={
              <AutoAwesomeIcon
                style={{ height: 13, width: 13, color: "var(--pill-muted)" }}
              />
            }
          />
        </>
      )}
      {settings.state.showPrice && tcgPrice && name && (
        <Price
          title="TCG Player prices"
          value={tcgPrice}
          link={tcgLink(name.replace(/ /g, "+"))}
          favicon={
            <img
              style={{ height: 10, width: 10 }}
              src="https://www.tcgplayer.com/favicon.ico"
              alt="tcg player"
            />
          }
        />
      )}
      {ignoreEnabled && (
        <button
          type="button"
          title="always hide this card"
          className="card-pill danger"
          onClick={(e) => {
            e.stopPropagation()
            if (name) addToIgnored(name)
          }}
          style={{ top: 146 }}
        >
          always hide
        </button>
      )}
      <img
        key={imgSrc} // induce instant feedback when switching
        style={{
          borderRadius: cardRadius,
          boxShadow: shadow ? "var(--card-shadow)" : undefined,
          ...dimensions,
        }}
        src={hasBeenInView ? imgSrc : undefined}
        onError={(e) => {
          if (!triedFallback.current) {
            triedFallback.current = true
            e.currentTarget.src = imgSrc.replace(/\.jpg$/, "-dtr.jpg")
          }
        }}
        alt={name}
      />
      {idxUrl && (
        <button
          className="see-index-button"
          onClick={(e) => {
            e.stopPropagation()
            setModal(true)
            event("card-index", "/deck")
          }}
        >
          <img alt="index app logo" src={IdxLogo} />
        </button>
      )}

      {/* manual attribute labeling — dev mode only */}
      {isDev && name && <DevLabelPanel name={name} />}

      {modal && (
        <Modal
          open
          onClose={() => setModal(false)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100vw",
            }}
          >
            <button
              className="index-modal-close"
              style={returnToBuilderStyles()}
              onClick={(e) => {
                e.stopPropagation()
                setModal(false)
              }}
            >
              Close
              <CloseIcon
                style={{
                  height: 20,
                  width: 20,
                  border: "1px solid var(--text)",
                  borderRadius: "50%",
                  padding: 2,
                }}
              />
            </button>
            <iframe
              className="index-modal-frame"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                border: "none",
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              }}
              title="grand-archive-index"
              src={idxUrl}
            />
          </Box>
        </Modal>
      )}
    </div>
  )

  // only check once
  const isOnlyOneLimit = useMemo(
    () => isSingleOnlyCard(card),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  if (ctx) {
    return (
      <InventoryWrapper
        isOnlyOneLimit={isOnlyOneLimit}
        enabled={ctx.inventoryEnabled}
        count={ctx.inventory[card.name]}
        remove={() =>
          ctx.setInventory((curr) => ({
            ...curr,
            [card.name]: Math.max((curr[card.name] ?? 0) - 1, 0),
          }))
        }
        add={() =>
          ctx.setInventory((curr) => ({
            ...curr,
            [card.name]: (curr[card.name] ?? 0) + 1,
          }))
        }
      >
        {cardComp}
      </InventoryWrapper>
    )
  }

  return cardComp
}
